import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PaymentStep = ({ onNext, onBack, paymentData, setPaymentData }) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(paymentData.selectedMethod || '');
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState(paymentData.billingAddress || 'same');
  const [promoCode, setPromoCode] = useState(paymentData.promoCode || '');
  const [promoApplied, setPromoApplied] = useState(false);
  const [errors, setErrors] = useState({});
  const [newCard, setNewCard] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: ''
  });

  const savedPaymentMethods = [
    {
      id: 1,
      type: 'card',
      brand: 'visa',
      last4: '4242',
      expiryMonth: '12',
      expiryYear: '2025',
      isDefault: true
    },
    {
      id: 2,
      type: 'card',
      brand: 'mastercard',
      last4: '8888',
      expiryMonth: '08',
      expiryYear: '2026',
      isDefault: false
    }
  ];

  const billingAddresses = [
    { id: 'same', label: 'Same as delivery address' },
    { id: 1, label: 'Home - 123 Main Street, New York, NY 10001' },
    { id: 2, label: 'Office - 456 Business Ave, New York, NY 10002' }
  ];

  const getCardIcon = (brand) => {
    switch (brand) {
      case 'visa':
        return 'CreditCard';
      case 'mastercard':
        return 'CreditCard';
      case 'amex':
        return 'CreditCard';
      default:
        return 'CreditCard';
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedPaymentMethod && !showNewCardForm) {
      newErrors.payment = 'Please select a payment method';
    }

    if (showNewCardForm) {
      if (!newCard.cardNumber.trim()) newErrors.cardNumber = 'Card number is required';
      if (!newCard.expiryDate.trim()) newErrors.expiryDate = 'Expiry date is required';
      if (!newCard.cvv.trim()) newErrors.cvv = 'CVV is required';
      if (!newCard.cardholderName.trim()) newErrors.cardholderName = 'Cardholder name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateForm()) {
      const paymentInfo = {
        selectedMethod: showNewCardForm ? newCard : selectedPaymentMethod,
        billingAddress: selectedBillingAddress,
        promoCode: promoApplied ? promoCode : '',
        isNewCard: showNewCardForm
      };
      
      setPaymentData(paymentInfo);
      onNext();
    }
  };

  const handlePaymentMethodSelect = (methodId) => {
    setSelectedPaymentMethod(methodId);
    setShowNewCardForm(false);
    setErrors({ ...errors, payment: '' });
  };

  const handleNewCardToggle = () => {
    setShowNewCardForm(!showNewCardForm);
    setSelectedPaymentMethod('');
    setErrors({ ...errors, payment: '' });
  };

  const handleNewCardChange = (field, value) => {
    setNewCard({ ...newCard, [field]: value });
    setErrors({ ...errors, [field]: '' });
  };

  const handleApplyPromoCode = () => {
    if (promoCode.trim()) {
      // Mock promo code validation
      if (promoCode.toUpperCase() === 'SAVE10') {
        setPromoApplied(true);
        setErrors({ ...errors, promoCode: '' });
      } else {
        setErrors({ ...errors, promoCode: 'Invalid promo code' });
      }
    }
  };

  const handleRemovePromoCode = () => {
    setPromoApplied(false);
    setPromoCode('');
  };

  return (
    <div className="space-y-8">
      {/* Payment Method Section */}
      <div>
        <h3 className="font-heading font-heading-semibold text-lg text-text-primary mb-4">
          Payment Method
        </h3>
        
        {/* Saved Payment Methods */}
        <div className="space-y-3 mb-4">
          {savedPaymentMethods.map((method) => (
            <div
              key={method.id}
              className={`
                p-4 border rounded-card cursor-pointer transition-smooth
                ${selectedPaymentMethod === method.id 
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }
              `}
              onClick={() => handlePaymentMethodSelect(method.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${selectedPaymentMethod === method.id 
                      ? 'border-primary bg-primary' :'border-border'
                    }
                  `}>
                    {selectedPaymentMethod === method.id && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <Icon name={getCardIcon(method.brand)} size={24} className="text-text-secondary" />
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-body-semibold text-text-primary capitalize">
                        {method.brand} •••• {method.last4}
                      </span>
                      {method.isDefault && (
                        <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-input">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-text-secondary text-sm">
                      Expires {method.expiryMonth}/{method.expiryYear}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  className="p-1"
                  iconName="Edit"
                  iconSize={16}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Add New Card Button */}
        <Button
          variant="outline"
          onClick={handleNewCardToggle}
          iconName="Plus"
          iconPosition="left"
          className="mb-4"
        >
          Add New Card
        </Button>

        {/* New Card Form */}
        {showNewCardForm && (
          <div className="p-4 border border-border rounded-card bg-background space-y-4">
            <h4 className="font-body-semibold text-text-primary">Add New Card</h4>
            
            <div>
              <Input
                type="text"
                placeholder="Card Number"
                value={newCard.cardNumber}
                onChange={(e) => handleNewCardChange('cardNumber', e.target.value)}
                className={errors.cardNumber ? 'border-error' : ''}
              />
              {errors.cardNumber && (
                <p className="text-error text-sm mt-1">{errors.cardNumber}</p>
              )}
            </div>

            <div>
              <Input
                type="text"
                placeholder="Cardholder Name"
                value={newCard.cardholderName}
                onChange={(e) => handleNewCardChange('cardholderName', e.target.value)}
                className={errors.cardholderName ? 'border-error' : ''}
              />
              {errors.cardholderName && (
                <p className="text-error text-sm mt-1">{errors.cardholderName}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  type="text"
                  placeholder="MM/YY"
                  value={newCard.expiryDate}
                  onChange={(e) => handleNewCardChange('expiryDate', e.target.value)}
                  className={errors.expiryDate ? 'border-error' : ''}
                />
                {errors.expiryDate && (
                  <p className="text-error text-sm mt-1">{errors.expiryDate}</p>
                )}
              </div>
              
              <div>
                <Input
                  type="text"
                  placeholder="CVV"
                  value={newCard.cvv}
                  onChange={(e) => handleNewCardChange('cvv', e.target.value)}
                  className={errors.cvv ? 'border-error' : ''}
                />
                {errors.cvv && (
                  <p className="text-error text-sm mt-1">{errors.cvv}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {errors.payment && (
          <p className="text-error text-sm mt-2">{errors.payment}</p>
        )}
      </div>

      {/* Billing Address Section */}
      <div>
        <h3 className="font-heading font-heading-semibold text-lg text-text-primary mb-4">
          Billing Address
        </h3>
        
        <div className="space-y-3">
          {billingAddresses.map((address) => (
            <div
              key={address.id}
              className={`
                p-4 border rounded-card cursor-pointer transition-smooth
                ${selectedBillingAddress === address.id 
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }
              `}
              onClick={() => setSelectedBillingAddress(address.id)}
            >
              <div className="flex items-center space-x-3">
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center
                  ${selectedBillingAddress === address.id 
                    ? 'border-primary bg-primary' :'border-border'
                  }
                `}>
                  {selectedBillingAddress === address.id && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <span className="font-body-semibold text-text-primary">
                  {address.label}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Promo Code Section */}
      <div>
        <h3 className="font-heading font-heading-semibold text-lg text-text-primary mb-4">
          Promo Code
        </h3>
        
        {!promoApplied ? (
          <div className="flex space-x-3">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                className={errors.promoCode ? 'border-error' : ''}
              />
              {errors.promoCode && (
                <p className="text-error text-sm mt-1">{errors.promoCode}</p>
              )}
            </div>
            <Button
              variant="outline"
              onClick={handleApplyPromoCode}
              disabled={!promoCode.trim()}
            >
              Apply
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between p-3 bg-success/10 border border-success/20 rounded-card">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={20} className="text-success" />
              <span className="font-body-semibold text-success">
                Promo code "{promoCode}" applied
              </span>
            </div>
            <Button
              variant="ghost"
              onClick={handleRemovePromoCode}
              iconName="X"
              iconSize={16}
              className="text-success hover:text-success"
            />
          </div>
        )}
      </div>

      {/* Security Notice */}
      <div className="flex items-center space-x-3 p-4 bg-background rounded-card">
        <Icon name="Shield" size={24} className="text-success" />
        <div>
          <p className="font-body-semibold text-text-primary">Secure Payment</p>
          <p className="text-text-secondary text-sm">
            Your payment information is encrypted and secure
          </p>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onBack}
          iconName="ArrowLeft"
          iconPosition="left"
        >
          Back to Delivery
        </Button>
        
        <Button
          variant="primary"
          onClick={handleContinue}
          iconName="ArrowRight"
          iconPosition="right"
          className="px-8"
        >
          Review Order
        </Button>
      </div>
    </div>
  );
};

export default PaymentStep;